import React, { useState, useEffect } from 'react';
import { ConfirmationPopUp } from '../ConfirmationPopUp/ConfirmationPopUp';
import ArchiveIcon from '@mui/icons-material/Archive';
import { Typography, FormControl, InputLabel, Select, MenuItem, TextField,Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as PXBColors from '@brightlayer-ui/colors';
import { isDataLoaded } from '../../types';
import { sortObjectArrayNaturally } from '../../lib/sort-naturally';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { Thunks } from '../../actions';
import { useAuthHelper } from '../AuthContextProvider';
import { useDispatch } from 'react-redux';
import { useSelectedLocationData, RecordDataState, useRefreshLocationList } from '../../hooks/data-hooks';

type ExportProps = {
    for: string;
    siteDetails?: { name: string; id: string };
    open: boolean;
    onClose: Function;
};
const useStyles = makeStyles(() => ({
    largeIcon: {
        width: 60,
        height: 60,
        color: PXBColors.gray[500],
    },
    formControl: {
        minWidth: 120,
        marginBottom: 40,
    },
}));

const SITE_SUMMARY_EXPORT = 'Site';

export const ExportPopUp: React.FC<ExportProps> = (props): JSX.Element => {
    const classes = useStyles();
    const { t } = useLanguageLocale();
    const AuthHelper = useAuthHelper();
    const Dispatch = useDispatch();
    const locationListData = useRefreshLocationList();

    const selectedLocationData = useSelectedLocationData();
    const [timeRange, setTimeRange] = useState(15);
    const getCurrentLocationId = (): string => {
        switch (selectedLocationData.state) {
            case RecordDataState.Found:
                return selectedLocationData.value.id;
            default:
                return '0';
        }
    };
    const [locationId, setlocationId] = useState(getCurrentLocationId());

    useEffect(() => {
        if (props.for === SITE_SUMMARY_EXPORT) if (props.siteDetails) setlocationId(props.siteDetails.id);
        const id = getCurrentLocationId();
        if (id === '0' && isDataLoaded(locationListData)) setlocationId(locationListData.value.organizationId);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOk = React.useCallback(() => {
        Dispatch(Thunks.TimelineExport.timelineExportData(locationId, timeRange, AuthHelper));
        props.onClose();
    }, [locationId, timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

    const allLocationItem = isDataLoaded(locationListData)
        ? [{ label: t('DASHBOARD.ALL_LOCATIONS'), id: locationListData.value.organizationId }]
        : undefined;

    const menuItems = isDataLoaded(locationListData)
        ? [
            ...sortObjectArrayNaturally(locationListData.value.locations, [{ sortField: 'locationName' }]).map(
                (location) => ({
                    label: location.locationName,
                    id: location.locationId,
                })
            ),
        ]
        : undefined;
    const dropdownList =
        menuItems && allLocationItem && menuItems.length > 1 ? [...allLocationItem, ...menuItems] : menuItems;
    const body = (): JSX.Element => (
        <>
            <Typography variant="h5">{t('TIMELINE_EXPORT.DOWNLOAD_TITLE')}</Typography>
            <br />
            <Typography variant="subtitle1">
                {props.for !== SITE_SUMMARY_EXPORT
                    ? t('TIMELINE_EXPORT.DOWNLOAD_SUBTITLE_TIMELINE')
                    : t('TIMELINE_EXPORT.DOWNLOAD_SUBTITLE_SITE')}
            </Typography>
            <br></br>
            <Box sx={{ '& .MuiFormControl-root': { margin: '16px 0px 36px 0px' }, }} >
            <FormControl variant="filled" className={classes.formControl} data-testid="location-dropdown">
                {props.for !== SITE_SUMMARY_EXPORT && (
                    <>
                        <InputLabel>Location</InputLabel>
                        <Select
                            style={{ width: '400px' }}
                            value={locationId}
                            onChange={(e): void => {
                                setlocationId(String(e.target.value));
                            }}
                        >
                            {dropdownList &&
                                dropdownList.map(
                                    (item: { label: string; id: string } | { label: string; id: number }) => (
                                        <MenuItem key={item.id} value={item.id}>
                                            {item.label}
                                        </MenuItem>
                                    )
                                )}
                        </Select>
                    </>
                )}
                {props.for === SITE_SUMMARY_EXPORT && (
                    <>
                        <TextField
                            label="Site"
                            style={{ width: '400px' }}
                            value={props.siteDetails && props.siteDetails.name}
                            InputProps={{
                                readOnly: true,
                            }}
                            variant="filled"
                        />
                    </>
                )}
            </FormControl>
            <FormControl variant="filled" className={classes.formControl}  data-testid="timerange-dropdown">
                <InputLabel>Time Range</InputLabel>
                <Select
                    style={{ width: '400px' }}
                    value={timeRange}
                    onChange={(e): void => {
                        setTimeRange(Number(e.target.value));
                    }}
                >
                    <MenuItem value={15}>15 Days</MenuItem>
                    <MenuItem value={30}>30 Days</MenuItem>
                    <MenuItem value={60}>60 Days</MenuItem>
                    <MenuItem value={90}>90 Days</MenuItem>
                </Select>
            </FormControl>
            </Box>
        </>
    );
    return (
        <ConfirmationPopUp
            icon={<ArchiveIcon className={classes.largeIcon} />}
            body={body()}
            okButtonText={t('ACTIONS.DOWNLOAD_DATA')}
            cancelButtonText={t('ACTIONS.CANCEL')}
            show={props.open}
            closeDialog={props.onClose}
            handleOk={handleOk}
            width="xs"
            cancelButtonsShow={true}
            okButtonShow={true}
        ></ConfirmationPopUp>
    );
};
